import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { action } from "typesafe-actions";
import { api } from "../../api/api";
import { IQueryParams } from "../common/common.types";
import { getSearchQuery } from "../common/helpers";
import { IStoreState } from "..";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import {
  getFileNameFromUrl,
  uploadMultipleFile,
} from "../../components/FileUpload/utils";
import { getUniqueId } from "../../helpers";
import produce from "immer";
import { IFileUpload } from "../../components/FileUpload/FileUpload.type";
import { server_base_endpoints } from "../../api/server-base-endpoints";
import {
  IGetPurchaseInvoiceResponse,
  IPurchaseInvoice,
} from "./purchase-invoice.types";

export const FETCH_PURCHASE_INVOICE_LIST_PROGRESS =
  "FETCH_PURCHASE_INVOICE_LIST_PROGRESS";
export const FETCH_PURCHASE_INVOICE_LIST_SUCCESS =
  "FETCH_PURCHASE_INVOICE_LIST_SUCCESS";
export const FETCH_PURCHASE_INVOICE_LIST_FAILED =
  "FETCH_PURCHASE_INVOICE_LIST_FAILED";

export const fetchPurchaseInvoiceListProgress = () =>
  action(FETCH_PURCHASE_INVOICE_LIST_PROGRESS);
export const fetchPurchaseInvoiceListSuccess = (
  data: IPurchaseInvoice[],
  totalRecords: number,
) => action(FETCH_PURCHASE_INVOICE_LIST_SUCCESS, { data, totalRecords });
export const fetchPurchaseInvoiceListFailed = () =>
  action(FETCH_PURCHASE_INVOICE_LIST_FAILED);

export const fetchPurchaseInvoiceListAsync =
  (
    queryParams: IQueryParams,
    module: "GENERAL" | "APPROVAL",
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      const searchQuery = getSearchQuery(queryParams);
      dispatch(fetchPurchaseInvoiceListProgress());
      let url = `${server_base_endpoints.purchase_invoice.get_purchase_invoice}${searchQuery}`;
      if (module === "APPROVAL") {
        url = `/dynamicApproval/get-approval${searchQuery}&table_name=latest_purchase_invoice`;
      }
      const res = await api.get(url);
      const data: IPurchaseInvoice[] = res.data.data;
      const totalRecords = res.data.totalRecords;
      dispatch(fetchPurchaseInvoiceListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchPurchaseInvoiceListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message ||
            "Failed to fetch Purchase Invoice list",
          displayAs: "snackbar",
        }),
      );
    }
  };

export const upsertSinglePurchaseInvoiceAsync =
  (
    payload: IPurchaseInvoice,
    onCallback: (
      isSuccess: boolean,
      purchaseInvoice?: IPurchaseInvoice,
    ) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const asPayload = {
        title: "Purchase Invoice Attachment",
      };

      const { paths } = await uploadMultipleFile(
        [],
        "PURCHASE_INVOICE",
        asPayload,
      );

      const {
        rowId,
        attachments,
        total_quantity,
        converted_quantity,
        insert_ts,
        create_ts,
        ...rest
      } = payload;

      const res = await api.post(
        server_base_endpoints.purchase_invoice.upsert_purchase_invoice,
        {
          ...rest,
          attachments: paths,
        },
      );
      const data = res.data.data;
      onCallback(true, data);
      dispatch(
        showMessage({
          type: "success",
          message: "Purchase Invoice saved successfully!",
          displayAs: "snackbar",
        }),
      );
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Failed to save purchase invoice",
          displayAs: "snackbar",
        }),
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const FETCH_SINGLE_PURCHASE_INVOICE_PROGRESS =
  "FETCH_SINGLE_PURCHASE_INVOICE_PROGRESS";
export const FETCH_SINGLE_PURCHASE_INVOICE_SUCCESS =
  "FETCH_SINGLE_PURCHASE_INVOICE_SUCCESS";
export const FETCH_SINGLE_PURCHASE_INVOICE_FAILED =
  "FETCH_SINGLE_PURCHASE_INVOICE_FAILED";

export const fetchSinglePurchaseInvoiceProgress = () =>
  action(FETCH_SINGLE_PURCHASE_INVOICE_PROGRESS);
export const fetchSinglePurchaseInvoiceSuccess = (data: IPurchaseInvoice) =>
  action(FETCH_SINGLE_PURCHASE_INVOICE_SUCCESS, { data });
export const fetchSinglePurchaseInvoiceFailed = (errorMessage: string) =>
  action(FETCH_SINGLE_PURCHASE_INVOICE_FAILED, { errorMessage });

export const fetchSinglePurchaseInvoiceAsync =
  (
    billing_company_uuid: string,
    uuid: string,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchSinglePurchaseInvoiceProgress());
      let url = `${server_base_endpoints.purchase_invoice.get_purchase_invoice}?purchase_invoice_uuid=${uuid}`;
      if (billing_company_uuid) {
        url += `&billing_company_uuid=${billing_company_uuid}`;
      }
      const res = await api.get(url);
      const data: IGetPurchaseInvoiceResponse[] = res.data.data;
      if (data.length > 0) {
        const { attachments, ...rest } = data[0];
        const newRespponse: IPurchaseInvoice = produce(
          { ...rest, attachments: [] as IFileUpload[] },
          (draftState) => {
            if (Array.isArray(attachments) && attachments.length > 0) {
              draftState.attachments = attachments.map((path) => ({
                key: getUniqueId(),
                file: null,
                type: "SERVER",
                name: getFileNameFromUrl(path as unknown as string),
                path: path,
              }));
            }
          },
        );
        dispatch(fetchSinglePurchaseInvoiceSuccess(newRespponse));
      } else {
        dispatch(
          fetchSinglePurchaseInvoiceFailed(
            "Oops! We couldn't find any records.",
          ),
        );
      }
    } catch (err: any) {
      dispatch(fetchSinglePurchaseInvoiceFailed("Something went wrong!"));
      dispatch(
        showMessage({
          type: "error",
          message:
            err.response?.data?.message || "Error fetching purchase invoice",
          displayAs: "snackbar",
        }),
      );
    }
  };

export const CLEAR_SINGLE_PURCHASE_INVOICE = "CLEAR_SINGLE_PURCHASE_INVOICE";
export const CLEAR_PURCHASE_INVOICE_STATE = "CLEAR_PURCHASE_INVOICE_STATE";

export const clearSinglePurchaseInvoice = () =>
  action(CLEAR_SINGLE_PURCHASE_INVOICE);
export const clearPurchaseInvoiceState = () =>
  action(CLEAR_PURCHASE_INVOICE_STATE);
